import config from '@/config';
import useAuthStore from '@/stores/authStore';

/**
 * Open an SSE stream to the AI agent endpoint and call event handlers as
 * data arrives.
 *
 * @param {object} params
 * @param {string}   params.projectId            - Project external UUID
 * @param {string}   params.message              - The user's new message
 * @param {Array}    params.conversation         - Prior turns [{role, content}]
 * @param {object}   [params.navigationContext]  - Current view context
 * @param {Function} [params.onText]             - Called with text chunk strings
 * @param {Function} [params.onThinking]         - Called with thinking chunk strings (AI_DEBUG_UI)
 * @param {Function} [params.onPlan]             - Called with array of upcoming action types
 * @param {Function} [params.onActionStarted]    - Called with (action_type) before tool runs
 * @param {Function} [params.onAction]           - Called with (action_type, data) after tool completes
 * @param {Function} [params.onDebugStats]       - Called with per-turn stats object (AI_DEBUG_UI)
 * @param {AbortSignal} [params.signal]           - Optional AbortSignal to cancel the stream
 * @param {Function} [params.onDone]             - Called when the agent finishes
 * @param {Function} [params.onError]            - Called with an error message string
 */
export const streamAgentResponse = async ({
  projectId,
  message,
  conversation,
  navigationContext,
  signal,
  onText,
  onThinking,
  onPlan,
  onActionStarted,
  onAction,
  onDebugStats,
  onDone,
  onError,
}) => {
  const csrfToken = useAuthStore.getState().csrfToken;

  let response;
  try {
    response = await fetch(`${config.serverUrl}/v1/projects/${projectId}/agent/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken || '',
      },
      credentials: 'include',
      signal: signal ?? null,
      body: JSON.stringify({
        message,
        conversation,
        navigation_context: navigationContext ?? null,
      }),
    });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') return;
    onError?.(`Network error: ${networkErr.message}`);
    return;
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.error || body?.detail || detail;
    } catch (_) {
      // ignore parse failure
    }
    onError?.(detail);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let aborted = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by blank lines (\n\n).
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // last entry may be an incomplete message

      for (const part of parts) {
        if (!part.trim()) continue;
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        let event;
        try {
          event = JSON.parse(dataLine.slice(6));
        } catch {
          console.warn('[Agent SSE] Unparseable line:', dataLine);
          continue;
        }

        if (event.type === 'text') {
          onText?.(event.content ?? '');
        } else if (event.type === 'thinking') {
          onThinking?.(event.content ?? '');
        } else if (event.type === 'plan') {
          onPlan?.(event.actions ?? []);
        } else if (event.type === 'action_started') {
          onActionStarted?.(event.action_type);
        } else if (event.type === 'action') {
          onAction?.(event.action_type, event.data);
        } else if (event.type === 'debug_stats') {
          onDebugStats?.(event.data ?? {});
        } else if (event.type === 'done') {
          onDone?.();
          return;
        } else if (event.type === 'error') {
          onError?.(event.content ?? 'Unknown error');
          return;
        }
      }
    }
  } catch (err) {
    // AbortError is user-initiated cancellation — exit silently.
    if (err.name !== 'AbortError') throw err;
    aborted = true;
  } finally {
    reader.releaseLock();
  }

  // Stream ended without an explicit done event (not applicable if aborted).
  if (!aborted) onDone?.();
};
